import React, { useState, useEffect } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  Chip,
  Alert,
  CircularProgress,
  Menu,
  MenuItem,
  FormControlLabel,
  Switch,
  Divider,
} from "@mui/material";
import {
  Add,
  Edit,
  Delete,
  MoreVert,
  Home,
  Person,
  Phone,
  LocationOn,
  Star,
  StarBorder,
  AccountBalance,
} from "@mui/icons-material";
import { AddressService } from "../services/AddressService";

const AddressManagement = ({ user, primaryBlue }) => {
  const [addresses, setAddresses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingAddress, setEditingAddress] = useState(null);
  const [formData, setFormData] = useState({
    fullName: "",
    addressLine1: "",
    addressLine2: "",
    city: "",
    state: "",
    postalCode: "",
    phoneNumber: "",
    label: "",
    isPrimary: false,
  });
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [selectedAddress, setSelectedAddress] = useState(null);
  const [upiId, setUpiId] = useState("");
  const [hasUpiId, setHasUpiId] = useState(false);
  const [upiLoading, setUpiLoading] = useState(false);

  useEffect(() => {
    fetchAddresses();
    fetchUpiId();
  }, [user.uid]);

  const fetchUpiId = async () => {
    try {
      const result = await AddressService.getUserUpiId(user.uid);
      if (result.success && result.data) {
        setUpiId(result.data.upiId || "");
        setHasUpiId(!!result.data.upiId);
      }
    } catch (error) {
      console.error("Error fetching UPI ID:", error);
    }
  };

  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => setSuccess(""), 5000);
      return () => clearTimeout(timer);
    }
  }, [success]);

  const fetchAddresses = async () => {
    try {
      setLoading(true);
      const result = await AddressService.getUserAddresses(user.uid);
      
      if (result.success) {
        setAddresses(result.data);
      } else {
        setError(result.error || "Failed to load addresses");
      }
    } catch (error) {
      setError("Failed to load addresses");
      console.error("Error fetching addresses:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field) => (event) => {
    setFormData(prev => ({
      ...prev,
      [field]: event.target.value
    }));
  };

  const handleSwitchChange = (field) => (event) => {
    setFormData(prev => ({
      ...prev,
      [field]: event.target.checked
    }));
  };

  const resetForm = () => {
    setFormData({
      fullName: "",
      addressLine1: "",
      addressLine2: "",
      city: "",
      state: "",
      postalCode: "",
      phoneNumber: "",
      label: "",
      isPrimary: false,
    });
    setEditingAddress(null);
  };

  const handleAddNew = () => {
    resetForm();
    setDialogOpen(true);
  };

  const handleEdit = (address) => {
    setEditingAddress(address);
    setFormData({
      fullName: address.fullName || "",
      addressLine1: address.addressLine1 || "",
      addressLine2: address.addressLine2 || "",
      city: address.city || "",
      state: address.state || "",
      postalCode: address.postalCode || "",
      phoneNumber: address.phoneNumber || "",
      label: address.label || "",
      isPrimary: address.isPrimary || false,
    });
    setDialogOpen(true);
    setMenuAnchor(null);
  };

  const handleSave = async () => {
    try {
      setError("");
      
      const validation = AddressService.validateAddress(formData);
      if (!validation.isValid) {
        setError(validation.errors[0]);
        return;
      }

      const addressData = {
        ...formData,
        userId: user.uid,
      };

      let result;
      if (editingAddress) {
        result = await AddressService.updateAddress(editingAddress.id, addressData);
      } else {
        result = await AddressService.addAddress(addressData);
      }

      if (result.success) {
        setSuccess(result.message);
        setDialogOpen(false);
        resetForm();
        fetchAddresses();
      } else {
        setError(result.error);
      }
    } catch (error) {
      setError("Failed to save address");
      console.error("Error saving address:", error);
    }
  };

  const handleDelete = async (addressId) => {
    if (!window.confirm("Are you sure you want to delete this address?")) {
      return;
    }

    try {
      setError("");
      const result = await AddressService.deleteAddress(addressId);
      
      if (result.success) {
        setSuccess(result.message);
        fetchAddresses();
      } else {
        setError(result.error);
      }
    } catch (error) {
      setError("Failed to delete address");
      console.error("Error deleting address:", error);
    } finally {
      setMenuAnchor(null);
    }
  };

  const handleSetPrimary = async (addressId) => {
    try {
      setError("");
      const result = await AddressService.setPrimaryAddress(user.uid, addressId);
      
      if (result.success) {
        setSuccess(result.message);
        fetchAddresses();
      } else {
        setError(result.error);
      }
    } catch (error) {
      setError("Failed to set primary address");
      console.error("Error setting primary address:", error);
    } finally {
      setMenuAnchor(null);
    }
  };

  const handleMenuOpen = (event, address) => {
    setMenuAnchor(event.currentTarget);
    setSelectedAddress(address);
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
    setSelectedAddress(null);
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" p={3}>
        <CircularProgress />
      </Box>
    );
  }

  const handleSaveUpiId = async () => {
    try {
      setUpiLoading(true);
      setError("");

      if (!upiId.trim()) {
        setError("UPI ID is required");
        return;
      }

      if (!/^[\w.-]+@[\w.-]+$/.test(upiId.trim())) {
        setError("Please enter a valid UPI ID (e.g., username@upi)");
        return;
      }

      const result = await AddressService.saveUserUpiId(user.uid, upiId.trim());

      if (result.success) {
        setSuccess(result.message);
        setHasUpiId(true);
      } else {
        setError(result.error);
      }
    } catch (error) {
      setError("Failed to save UPI ID");
      console.error("Error saving UPI ID:", error);
    } finally {
      setUpiLoading(false);
    }
  };

  return (
    <Box>
      {/* UPI ID Section */}
      <Card sx={{ mb: 3, p: 3 }}>
        <Typography variant="h6" sx={{ color: primaryBlue[800], fontWeight: 700, mb: 2 }}>
          UPI ID for Payments
        </Typography>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={8}>
            <TextField
              fullWidth
              label="UPI ID"
              value={upiId}
              onChange={(e) => setUpiId(e.target.value)}
              disabled={hasUpiId}
              placeholder="Enter your UPI ID (e.g., username@upi)"
              helperText={
                hasUpiId
                  ? "UPI ID can only be set once. Contact admin to change."
                  : "Your UPI ID for receiving payments"
              }
              InputProps={{
                startAdornment: (
                  <AccountBalance sx={{ mr: 1, color: "text.secondary" }} />
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <Button
              fullWidth
              variant="contained"
              onClick={handleSaveUpiId}
              disabled={hasUpiId || upiLoading}
              sx={{
                background: `linear-gradient(45deg, ${primaryBlue[600]}, ${primaryBlue[500]})`,
                fontWeight: 600,
              }}
            >
              {upiLoading ? <CircularProgress size={24} /> : hasUpiId ? "UPI ID Saved" : "Save UPI ID"}
            </Button>
          </Grid>
        </Grid>
      </Card>

      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h6" sx={{ color: primaryBlue[800], fontWeight: 700 }}>
          Saved Addresses
        </Typography>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={handleAddNew}
          sx={{
            background: `linear-gradient(45deg, ${primaryBlue[600]}, ${primaryBlue[500]})`,
            fontWeight: 600,
          }}
        >
          Add Address
        </Button>
      </Box>

      {/* Error/Success Messages */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      
      {success && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {success}
        </Alert>
      )}

      {/* Address List */}
      {addresses.length === 0 ? (
        <Card sx={{ textAlign: "center", p: 4 }}>
          <Home sx={{ fontSize: 64, color: primaryBlue[300], mb: 2 }} />
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No addresses saved yet
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Add your first address to save time during checkout
          </Typography>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={handleAddNew}
            sx={{
              background: `linear-gradient(45deg, ${primaryBlue[600]}, ${primaryBlue[500]})`,
            }}
          >
            Add Your First Address
          </Button>
        </Card>
      ) : (
        <Grid container spacing={2}>
          {addresses.map((address) => (
            <Grid item xs={12} md={6} key={address.id}>
              <Card 
                sx={{ 
                  position: "relative",
                  border: address.isPrimary ? `2px solid ${primaryBlue[500]}` : "1px solid #e0e0e0",
                  transition: "all 0.2s ease",
                  "&:hover": {
                    boxShadow: 4,
                    transform: "translateY(-2px)",
                  }
                }}
              >
                <CardContent>
                  {/* Primary Badge & Menu */}
                  <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={1}>
                    <Box>
                      {address.isPrimary && (
                        <Chip
                          icon={<Star />}
                          label="Primary"
                          size="small"
                          color="primary"
                          sx={{ mb: 1 }}
                        />
                      )}
                      {address.label && (
                        <Chip
                          label={address.label}
                          size="small"
                          variant="outlined"
                          sx={{ mb: 1, ml: address.isPrimary ? 1 : 0 }}
                        />
                      )}
                    </Box>
                    <IconButton
                      size="small"
                      onClick={(e) => handleMenuOpen(e, address)}
                    >
                      <MoreVert />
                    </IconButton>
                  </Box>

                  {/* Address Details */}
                  <Box>
                    <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                      <Person sx={{ fontSize: 16, mr: 1, verticalAlign: "text-bottom" }} />
                      {address.fullName}
                    </Typography>
                    
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      <LocationOn sx={{ fontSize: 16, mr: 1, verticalAlign: "text-bottom" }} />
                      {address.addressLine1}
                      {address.addressLine2 && `, ${address.addressLine2}`}
                    </Typography>
                    
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      {address.city}, {address.state} {address.postalCode}
                    </Typography>
                    
                    <Typography variant="body2" color="text.secondary">
                      <Phone sx={{ fontSize: 16, mr: 1, verticalAlign: "text-bottom" }} />
                      {address.phoneNumber}
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Context Menu */}
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={handleMenuClose}
      >
        {selectedAddress && !selectedAddress.isPrimary && (
          <MenuItem onClick={() => handleSetPrimary(selectedAddress.id)}>
            <Star sx={{ mr: 1 }} />
            Set as Primary
          </MenuItem>
        )}
        <MenuItem onClick={() => handleEdit(selectedAddress)}>
          <Edit sx={{ mr: 1 }} />
          Edit
        </MenuItem>
        <MenuItem 
          onClick={() => handleDelete(selectedAddress.id)}
          sx={{ color: "error.main" }}
        >
          <Delete sx={{ mr: 1 }} />
          Delete
        </MenuItem>
      </Menu>

      {/* Add/Edit Dialog */}
      <Dialog 
        open={dialogOpen} 
        onClose={() => setDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {editingAddress ? "Edit Address" : "Add New Address"}
        </DialogTitle>
        
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Full Name"
                value={formData.fullName}
                onChange={handleInputChange("fullName")}
                placeholder="Enter full name for delivery"
              />
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Address Line 1"
                value={formData.addressLine1}
                onChange={handleInputChange("addressLine1")}
                placeholder="House number, street name"
              />
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Address Line 2 (Optional)"
                value={formData.addressLine2}
                onChange={handleInputChange("addressLine2")}
                placeholder="Apartment, suite, landmark"
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="City"
                value={formData.city}
                onChange={handleInputChange("city")}
                placeholder="Enter city"
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="State"
                value={formData.state}
                onChange={handleInputChange("state")}
                placeholder="Enter state"
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Postal Code"
                value={formData.postalCode}
                onChange={handleInputChange("postalCode")}
                placeholder="6-digit PIN code"
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Phone Number"
                value={formData.phoneNumber}
                onChange={handleInputChange("phoneNumber")}
                placeholder="Contact number for delivery"
              />
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Label (Optional)"
                value={formData.label}
                onChange={handleInputChange("label")}
                placeholder="e.g., Home, Office, etc."
              />
            </Grid>

            <Grid item xs={12}>
              <Divider sx={{ my: 1 }} />
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.isPrimary}
                    onChange={handleSwitchChange("isPrimary")}
                    color="primary"
                  />
                }
                label="Set as primary address"
              />
            </Grid>
          </Grid>
        </DialogContent>
        
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>
            Cancel
          </Button>
          <Button 
            onClick={handleSave} 
            variant="contained"
            sx={{
              background: `linear-gradient(45deg, ${primaryBlue[600]}, ${primaryBlue[500]})`,
            }}
          >
            {editingAddress ? "Update Address" : "Add Address"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AddressManagement;